import { showErrorToast } from '@bpinternal/ui-kit-next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useCurrentUser, useSetWorkspacePreferences } from '~/hooks'
import { getQueryKey } from '~/services'
import { resolveKey } from '~/services/preferences/preferences'
import { HttpError } from '~/shared'
import { mapErrorCodeToMessage } from '../helpers'
import { BillingApiClient } from '../services'
import { PlanProduct } from '../types/index'
import { isUpgrade } from '../utils/plan'
import { useActiveSubscription } from './useActiveSubscription'
import { useAddons } from './useAddons'
import { usePlanProducts } from './usePlanProducts'
import { useSaleEvent } from './useSaleEvent'
import { useSetPromoCode } from './useSetPromoCode'

export const usePurchasePlan = (workspaceId: string, onSuccess?: () => void) => {
  const user = useCurrentUser()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { monthly: monthlyActiveSubscription } = useActiveSubscription(workspaceId)
  const { currentPlan } = usePlanProducts(workspaceId)
  const { addons } = useAddons(workspaceId)
  const saleEvent = useSaleEvent(workspaceId)

  const { mutate: setWorkspacePreference } = useSetWorkspacePreferences(workspaceId)
  const { mutate: setPromoCode } = useSetPromoCode(workspaceId)

  return useMutation({
    gcTime: 0,
    mutationFn: async ({ desiredPlan }: { desiredPlan: PlanProduct; isYearly: boolean }) => {
      const addonQuantity = addons.map((addon) => ({
        price: addon.priceId,
        quantity: addon.nextPaymentQuantity,
      }))

      const planDiscounts = saleEvent?.planDiscounts[desiredPlan.legacyName]
      const couponId =
        desiredPlan.cycle === 'monthly' ? planDiscounts?.monthly?.couponId : planDiscounts?.yearly?.couponId

      if (monthlyActiveSubscription && desiredPlan.cycle === 'monthly') {
        if (saleEvent) {
          const promoCode = saleEvent.planDiscounts[desiredPlan.legacyName]?.monthly?.promoCode

          if (promoCode) {
            setPromoCode({ code: promoCode })
          }
        }

        await BillingApiClient.updateSubscription(workspaceId, {
          productQuantityConfig: [
            ...addonQuantity,
            {
              price: desiredPlan.priceId,
              quantity: 1,
            },
          ],
          subscriptionId: monthlyActiveSubscription.id,
        })
      } else {
        const { url } = await BillingApiClient.generateCheckoutSession(
          workspaceId,
          {
            [desiredPlan.id]: {
              priceId: desiredPlan.priceId,
              quantity: 1,
            },
          },
          user.email,
          couponId
        )
        window.location.href = url
      }
    },
    onError: (error) => {
      const httpError = error as HttpError
      showErrorToast(
        mapErrorCodeToMessage(httpError.data?.code) ??
          error.message ??
          'There was an error while updating your subscription. Please try again.'
      )
    },
    onSuccess: (_, { desiredPlan: newPlan }) => {
      // TODO: Remove this hack but otherwise I cannot know if the user is downgrading to community
      if (newPlan.legacyName === 'community') {
        setWorkspacePreference({
          path: 'cancelledToCommunity',
          value: {
            value: true,
            date: new Date(),
          },
        })
      } else {
        setWorkspacePreference({
          path: 'cancelledToCommunity',
          value: {
            value: false,
          },
        })
      }

      void queryClient.invalidateQueries({
        queryKey: getQueryKey('workspaces_/$workspaceId_/preferences/$path', {
          path: resolveKey({ path: 'downgradeToCommunity' }),
          workspaceId,
        }),
      })
      void queryClient.invalidateQueries({
        queryKey: getQueryKey('workspaces_/$workspaceId_/billing/subscriptions', { workspaceId }),
      })
      void queryClient.invalidateQueries({
        queryKey: getQueryKey('workspaces_/$workspaceId_/billing/subscriptionSchedule', { workspaceId }),
      })

      onSuccess?.()

      const shouldShowWelcomeDialog =
        monthlyActiveSubscription &&
        isUpgrade(currentPlan.legacyName, newPlan.legacyName) &&
        (newPlan.legacyName === 'plus' || newPlan.legacyName === 'team')

      void navigate({
        to: '/workspaces/$workspaceId/billing',
        params: { workspaceId },
        search: (prev) => ({
          ...prev,
          newPlan: shouldShowWelcomeDialog ? newPlan.legacyName : undefined,
        }),
      })
    },
    meta: {
      suppressError: true,
    },
  })
}
